import React, { useEffect, useState } from 'react';
import { getTheme, useThemeMode } from '../theme';
import { mcpApi, type McpConnection, type QidUser } from '../api';

/**
 * OAuth-godkännande för MCP-kopplingar (t.ex. Claude.ai). API:t skickar hit
 * användaren med /?oauth=<id> (apps/api/src/mcp/oauth.ts). Id:t sparas i
 * sessionStorage av App.tsx så att det överlever en inloggning via Qid.
 */
export function OAuthConsentModal({ requestId, user, onNeedLogin, onClose }: {
  requestId: string; user: QidUser | null; onNeedLogin: () => void; onClose: () => void;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [info, setInfo] = useState<{ clientName: string; redirectHost: string } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { mcpApi.oauthRequest(requestId).then(setInfo).catch(e => setError(e.message)); }, [requestId]);

  async function decide(approve: boolean) {
    setBusy(true); setError('');
    try {
      const redirectUrl = await mcpApi.oauthDecide(requestId, approve);
      try { sessionStorage.removeItem('qvenari_oauth_request'); } catch {}
      window.location.href = redirectUrl;
    }
    catch (e: any) { setError(e.message ?? 'Något gick fel'); setBusy(false); }
  }

  const btn = (primary: boolean): React.CSSProperties => ({
    padding: '10px 18px', borderRadius: 6, fontSize: 14, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
    border: primary ? 'none' : `1px solid ${T.border}`, background: primary ? T.amber : 'transparent', color: primary ? '#000' : T.text,
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: T.surface, borderRadius: 10, padding: 24, width: '100%', maxWidth: 440, border: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 19, fontWeight: 800, color: T.text, marginBottom: 12 }}>Anslut till Qvenari</div>
        {error && !info ? (
          <>
            <div style={{ fontSize: 14, color: '#e05a4f', marginBottom: 16 }}>{error}</div>
            <button style={btn(false)} onClick={onClose}>Stäng</button>
          </>
        ) : !info ? (
          <div style={{ fontSize: 13, color: T.textMuted }}>Laddar…</div>
        ) : (
          <>
            <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.6 }}>
              <strong style={{ color: T.text }}>{info.clientName}</strong> vill komma åt ditt Qvenari-konto: läsa dina jaktmarker,
              kalender, chatt, avskjutningsplan och jaktrapporter, samt — när du ber om det — planera jakter,
              skriva i chatten och registrera fällt vilt. Aldrig administratörsbehörighet.
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 10 }}>
              Du skickas sedan tillbaka till <strong>{info.redirectHost}</strong>. Kopplingen kan tas bort under din profil.
            </div>
            {error && <div style={{ fontSize: 13, color: '#e05a4f', marginTop: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20, flexWrap: 'wrap' }}>
              {user ? (
                <>
                  <button style={btn(false)} disabled={busy} onClick={() => decide(false)}>Neka</button>
                  <button style={btn(true)} disabled={busy} onClick={() => decide(true)}>{busy ? '…' : 'Tillåt'}</button>
                </>
              ) : (
                <>
                  <button style={btn(false)} onClick={() => decide(false)}>Avbryt</button>
                  <button style={btn(true)} onClick={onNeedLogin}>Logga in för att fortsätta</button>
                </>
              )}
            </div>
            {user && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 12, textAlign: 'right' }}>Inloggad som {user.email}</div>}
          </>
        )}
      </div>
    </div>
  );
}

/** Profil: anslutna AI-assistenter (MCP) med möjlighet att ta bort dem. */
export function McpConnectionsCard({ cardStyle }: { cardStyle: React.CSSProperties }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [list, setList] = useState<McpConnection[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => { mcpApi.connections().then(setList).catch(() => setList([])); }, []);

  async function revoke(c: McpConnection) {
    if (!confirm(`Ta bort kopplingen "${c.name}"? Assistenten kommer inte längre åt ditt konto.`)) return;
    try { await mcpApi.revoke(c.id); setList(prev => (prev ?? []).filter(x => x.id !== c.id)); }
    catch (e: any) { setError(e.message ?? 'Kunde inte ta bort'); }
  }

  const fmt = (ms: number) => new Date(ms).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
  return (
    <div style={{ ...cardStyle, marginBottom: 16 }}>
      <div style={{ color: T.textSub, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>AI-assistenter (MCP)</div>
      <div style={{ color: T.textSub, fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
        Lägg till Qvenari som anslutning i t.ex. Claude med adressen <code style={{ color: T.amber }}>https://api.qvenari.com/mcp</code>.
        Assistenten ser bara dina egna jaktmarker.
      </div>
      {list === null ? <div style={{ color: T.textMuted, fontSize: 13 }}>Laddar…</div>
        : list.length === 0 ? <div style={{ color: T.textMuted, fontSize: 13 }}>Inga anslutna assistenter.</div>
        : list.map(c => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 0', borderTop: `1px solid ${T.border}` }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>{c.name}</div>
              <div style={{ color: T.textMuted, fontSize: 12 }}>Ansluten {fmt(c.createdAt)}{c.lastUsedAt ? ` · senast använd ${fmt(c.lastUsedAt)}` : ''}</div>
            </div>
            <button onClick={() => revoke(c)} style={{ flexShrink: 0, padding: '7px 12px', borderRadius: 6, border: `1px solid ${T.border}`, background: 'transparent', color: T.text, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Ta bort</button>
          </div>
        ))}
      {error && <div style={{ color: '#e05a4f', fontSize: 13, marginTop: 8 }}>{error}</div>}
    </div>
  );
}
